import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, Observable, interval, BehaviorSubject, tap, of, catchError, map } from 'rxjs';
import { AppConstants } from 'src/app/constants/app-constants';
import { AuthService } from '../auth/auth.service';
import { UserService } from '../user/user.service';
import { UserGroup, UserGroupDto, UserGroupMapper } from 'src/app/model/UserGroup';
import { AbstractApiService } from '../abstract-api-service/abstract-api-service';
import { PermissionEntry, User } from 'src/app/model/User';
import { PERMISSIONS_ENDPOINT, USER_GROUPS_ADD_ENDPOINT, USER_GROUPS_ADD_GROUP_TO_USER, USER_GROUPS_GET_ENDPOINT, USER_GROUPS_PUT_ENDPOINT } from 'src/app/constants/api-routes';

@Injectable({
  providedIn: 'root'
})
export class GroupService extends AbstractApiService<UserGroup> {
  /** Observable that emits the latest list of groups */
  protected _subject = new Subject<UserGroup[]>();
  public changes$ = this._subject.asObservable();

  /** A list of groups the user has access to */
  protected _collection?: UserGroup[];

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /** List of all permissions in the system */
  private _supplierPermissions: PermissionEntry[] = [];
  private _borrowerPermissions: PermissionEntry[] = [];

  /**
   * Default constructor for the service, used to initialize services
   *
   * @param http
   * @param authService
   */
  constructor(
    private _userService: UserService,
    private _authService: AuthService,
    protected _http: HttpClient,
  ) {
    super();
    // On authentication changes, clear persisted data
    this._userService.user$.subscribe((user: User | null) => {
      if (this._authService.authenticated && user != undefined) {
        this._fetch();
      } else {
        this._collection = undefined;
        this._subject.next([]);
      }
    });

    // Continuously query the backend to receive new applications
    this._startTimer(0);
    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetch();
      });
  }

  /**
   * Get a list of groups
   *
   * @return A reference to the current collection
   */
  public getEntities(): UserGroup[] | undefined {
    return this._collection?.map(x => x.copyOf());
  }

  /**
   * Apply a group to a user
   *
   * @param userId The ID of the user to apply the group to
   * @param groupId The ID of the group to apply
   */
  public applyGroupToUser(userId: string, groupId: string): Observable<UserGroupDto> {
    // Get company id
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) {
      throw new Error("Cannot apply group to user, no company selected");
    }

    // Make request to apply the group to the user
    return this._http.put<UserGroupDto>(USER_GROUPS_ADD_GROUP_TO_USER(companyId, groupId, userId), {}).pipe(
      // Log success or error and continue propagating the observable
      tap({
        next: (_: UserGroupDto) => {
          console.log(`[GROUP]: successfully applied group ${groupId} to user ${userId}`);
        },
        error: (error) => {
          console.error(`[GROUP]: failed to apply group ${groupId} to user ${userId}`, error);
        }
      }),
    );
  }

  /**
   * Get a list of all permissions available in the system
   * 
   * @returns A list of all permissions
   */
  public getAllPermissions(): Observable<PermissionEntry[]> {
    // Get company role
    const role = this._userService.company?.companyType;
    if (role == undefined) {
      console.error(`[GROUP]: cannot fetch permissions, no company role found`);
      return of([]);
    }
    
    // If permissions are already initialized, return a copy of them
    if (role === "BORROWER" && this._borrowerPermissions.length > 0) {
      return of(this._borrowerPermissions.map(x => new PermissionEntry(x.id, x.companyId, x.permission)));
    } else if (role === "SUPPLIER" && this._supplierPermissions.length > 0) {
      return of(this._supplierPermissions.map(x => new PermissionEntry(x.id, x.companyId, x.permission)));
    }

    // Get company id
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) {
      console.error(`[GROUP]: cannot fetch permissions, no company selected`);
      return of([]);
    }

    // Initialize permissions
    return this._http.get<string[]>(PERMISSIONS_ENDPOINT(companyId, role)).pipe(
      // On success, set the permissions and return a copy of them as permission entries
      map((permissions: string[]): PermissionEntry[] => {
        if (role === 'SUPPLIER') {
          // If the role is supplier, set the supplier permissions
          this._supplierPermissions = permissions.map(p => new PermissionEntry(null, companyId, p));
          return this._supplierPermissions.map(x => new PermissionEntry(x.id, x.companyId, x.permission));
        } else if (role === 'BORROWER') {
          // If the role is borrower, set the borrower permissions
          this._borrowerPermissions = permissions.map(p => new PermissionEntry(null, companyId, p));
          return this._borrowerPermissions.map(x => new PermissionEntry(x.id, x.companyId, x.permission));
        } else {
          // If the role is unknown, log an error and return an empty array
          console.error(`[GROUP]: unknown company role ${role}, cannot fetch permissions`);
          return [];
        }
      }),
      // Catch errors and log them
      catchError((error) => {
        console.error(`[GROUP]: failed to fetch permissions`, error);
        return of([]);
      }),
    );
  }

  /**
   * Create a new group
   * 
   * @param group The group to create
   * @returns An observable that emits the created group
   */
  public createGroup(group: UserGroup): Observable<UserGroupDto> {
    // Get companyId
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) {
      throw new Error("Cannot create group, no company selected");
    }

    const groupDto = UserGroupMapper.toDto(group);

    // Make request to create the group
    return this._http.post<UserGroupDto>(USER_GROUPS_ADD_ENDPOINT(companyId), groupDto).pipe(
      tap({
        // On success, add the group to the collection and notify subscribers
        next: (createdGroup: UserGroupDto) => {
          console.log(`[GROUP]: successfully created group ${createdGroup.id}`);
          this.refresh(UserGroupMapper.toModel(createdGroup), 'create');
        },
        // On error, log the error
        error: (error) => {
          console.error(`[GROUP]: failed to create group`, error);
        }
      }),
    );
  }

  /**
   * Update a provided group
   * 
   * @param group The group to update
   * @returns An observable that emits the updated group
   */
  public updateGroup(group: UserGroup): Observable<UserGroupDto> {
    // Get companyId
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) {
      throw new Error("Cannot update group, no company selected");
    }

    const groupDto = UserGroupMapper.toDto(group);

    // Make request to update the group
    return this._http.put<UserGroupDto>(USER_GROUPS_PUT_ENDPOINT(companyId, group.id), groupDto).pipe(
      tap({
        // On success, update the group in the collection and notify subscribers
        next: (updatedGroup: UserGroupDto) => {
          console.log(`[GROUP]: successfully updated group ${group.id}`);
          this.refresh(UserGroupMapper.toModel(updatedGroup), 'modify');
        },
        // On error, log the error
        error: (error) => {
          console.error(`[GROUP]: failed to update group ${group.id}`, error);
        }
      }),
    );
  }

  /**
   * Set a list of people to the current collection
   *
   * @param people
   */
  private _setEntities(people: UserGroup[]): void {
    this._collection = people.map(x => x.copyOf());
    this._subject.next(people.map(x => x.copyOf()));
  }


  /**
   * Get groups from the backend. You should subscribe to the 'groups$'
   * field to receive updates
   */
  private _fetch(): void {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return;

    // Get company id
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) return;

    this._http.get<UserGroupDto[]>(USER_GROUPS_GET_ENDPOINT(companyId)).subscribe({
      next: (groupInterfaces: UserGroupDto[]): void => {
        if (groupInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into group list`);
        }

        // Convert the interfaces to class values
        let groups: UserGroup[] = [];
        for (let groupInt of groupInterfaces) {
          groups.push(UserGroupMapper.toModel(groupInt));
        }

        // Propagate changes if different
        this._setEntities(groups);
        console.log(`[INFO]: successfully queried groups`);
      },
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${(""+seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }
}

// Helper function to delay for a given number of milliseconds
const delay = (delay: number): Promise<void> => {
  return new Promise(r => {setTimeout(r, delay)});
}

